// CinematicScene — the full R3F scene tree for a WildQuest battle.
//   * Arena backdrop (per-biome ground, trees, mist, sky)
//   * Two animated creatures reacting to the battle event stream
//   * Weather particles (rain / snow / leaves / dust)
//   * Impact FX driven by `lastLineKind`
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import CreatureRig from './CreatureRig';
import ArenaEnvironment from './ArenaEnvironment';
import WeatherFx from './WeatherFx';
import ImpactFx from './ImpactFx';
import CameraOcclusion from './CameraOcclusion';
import { resolveSpecies, computeCombatPositions } from '../../lib/species_registry';

export default function CinematicScene({
  arena, weather, chSnap, opSnap, chHp, opHp,
  lastLineKind, cursor, lowPower, consumedItem,
}) {
  // Species-aware separation — larger animals need more room between them.
  const { chPos, opPos } = useMemo(
    () => computeCombatPositions(chSnap, opSnap),
    [chSnap, opSnap],
  );
  return (
    <>
      {/* Sky/fog + lighting */}
      <hemisphereLight args={[0xffe9c8, 0x0a1b0f, 0.5]} />
      <directionalLight
        position={[6, 10, 4]} intensity={1.15}
        castShadow={!lowPower}
        shadow-mapSize-width={1024} shadow-mapSize-height={1024}
        color="#ffe4b5"
      />
      <ambientLight intensity={0.28} color="#94b8ff" />

      <fog attach="fog" args={[colorForArena(arena, weather), 12, 60]} />
      <color attach="background" args={[colorForArena(arena, weather)]} />

      {/* Ground + biome dressing */}
      <ArenaEnvironment arena={arena} weather={weather} lowPower={lowPower} />

      {/* Combatants — position from species-aware separation */}
      <CreatureRig
        side="ch"
        snap={chSnap}
        position={chPos}
        facing={1}
        hp={chHp}
        lastLineKind={lastLineKind}
        cursor={cursor}
        auraColor={consumedItem?.color}
      />
      <CreatureRig
        side="op"
        snap={opSnap}
        position={opPos}
        facing={-1}
        hp={opHp}
        lastLineKind={lastLineKind}
        cursor={cursor}
      />

      {/* Weather */}
      {!lowPower && <WeatherFx weather={weather} arena={arena} />}

      {/* Impact particles keyed to the current event */}
      <ImpactFx kind={lastLineKind} cursor={cursor} />

      {/* Camera occlusion — fades any scenery mesh between camera and either
          creature so the player never loses sight of the action. */}
      <CameraOcclusion chPosition={chPos} opPosition={opPos} />
    </>
  );
}


// Palette per biome — kept tight so `<color background>` matches the fog.
function colorForArena(arena, weather) {
  const base = {
    forest:   '#0f2a1e',
    wetlands: '#0e2933',
    coast:    '#0a2b3a',
    mountain: '#1a1e2b',
    meadow:   '#1a2e18',
    desert:   '#3b2416',
    cave:     '#0a0a12',
    tundra:   '#20313e',
    snow:     '#22384a',
    river:    '#0f2432',
    lake:     '#0e2130',
    swamp:    '#111f18',
  }[arena] || '#0f1e17';
  if (weather === 'fog') return blend(base, '#3c4552', 0.35);
  if (weather === 'storm' || weather === 'rain') return blend(base, '#0a0e18', 0.25);
  if (weather === 'snow') return blend(base, '#dfe9ec', 0.15);
  return base;
}

function blend(a, b, t) {
  const A = new THREE.Color(a), B = new THREE.Color(b);
  return `#${A.lerp(B, t).getHexString()}`;
}
